import React, { useState } from 'react';
import { useDisclosure } from '@chakra-ui/hooks';
import { OKRState, Task, Objective, KPI, KPIPeriodicity, ActionPlan } from '../types';
import CreateActionPlanModal from './CreateActionPlanModal';
import {
    computeObjectiveProgress, computeKPIHealth, statusColors, statusLabels,
    priorityColors, priorityLabels, getTeamMemberName, healthColors,
    periodicityLabels, periodDateLabel,
} from '../helpers';
import { useOKRPermissions } from '../useOKRPermissions';
import KPICard from './KPICard';
import KPIHistoryTable from './KPIHistoryTable';
import TaskBoard from './TaskBoard';
import {
    ChevronDown, ChevronRight, Target, BarChart2, Clipboard, Zap,
    Calendar, User, DollarSign, TrendingUp, Pencil, Trash2, Plus, RefreshCw,
} from 'lucide-react';
import { MdClose } from 'react-icons/md';

const RECURRENCE_LABELS: Record<string, string> = {
    daily: 'Quotidien', weekly: 'Hebdomadaire', monthly: 'Mensuel',
};

// ── Inline KPI edit form ──────────────────────────────────────────────────────

const KPIEditRow: React.FC<{
    kpi: KPI;
    onSave: (id: string, data: Record<string, any>) => void;
    onDelete: (id: string) => void;
    onCancel: () => void;
    canEdit: boolean;
}> = ({ kpi, onSave, onDelete, onCancel, canEdit }) => {
    const [targetValue, setTargetValue] = useState(String(kpi.targetValue));
    const [minAcceptable, setMinAcceptable] = useState(String(kpi.minAcceptableValue));
    const [name, setName] = useState(kpi.name);
    const [unit, setUnit] = useState(kpi.unit);
    const [direction, setDirection] = useState(kpi.direction);
    const [periodicity, setPeriodicity] = useState<KPIPeriodicity>(kpi.periodicity ?? 'monthly');
    const [history, setHistory] = useState(() =>
        kpi.history.length > 0
            ? kpi.history
            : (kpi.currentValue ? [{ date: periodDateLabel(kpi.periodicity ?? 'monthly'), value: kpi.currentValue }] : [])
    );

    const sInput = 'w-full rounded-xl border border-gray-200 px-2.5 py-2 text-sm outline-none focus:border-toola-500';

    const handleSave = () => {
        onSave(kpi.id || kpi._id || '', {
            name: name.trim(),
            currentValue: history.length > 0 ? history[history.length - 1].value : kpi.currentValue,
            targetValue: parseFloat(targetValue) || 0,
            minAcceptableValue: parseFloat(minAcceptable) || 0,
            unit: unit.trim(),
            direction,
            periodicity,
            history,
        });
    };

    return (
        <div className="rounded-xl border border-toola-200 bg-blue-50/50 p-4 space-y-3">
            <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-toola-500">Modifier le KPI</p>
                <button onClick={onCancel} className="text-gray-400 hover:text-gray-600"><MdClose className="w-4 h-4" /></button>
            </div>
            <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Nom</label>
                <input value={name} onChange={(e) => setName(e.target.value)} className={sInput} />
            </div>
            <div className="grid grid-cols-2 gap-2">
                <div><label className="text-xs font-medium text-gray-600 mb-1 block">Cible</label><input type="number" value={targetValue} onChange={(e) => setTargetValue(e.target.value)} className={sInput} /></div>
                <div><label className="text-xs font-medium text-gray-600 mb-1 block">Min acceptable</label><input type="number" value={minAcceptable} onChange={(e) => setMinAcceptable(e.target.value)} className={sInput} /></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
                <div><label className="text-xs font-medium text-gray-600 mb-1 block">Unité</label><input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="%, €…" className={sInput} /></div>
                <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Sens</label>
                    <select value={direction} onChange={(e) => setDirection(e.target.value as any)} className={sInput}>
                        <option value="higher-is-better">↑ Plus haut = mieux</option>
                        <option value="lower-is-better">↓ Plus bas = mieux</option>
                    </select>
                </div>
                <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Rythme</label>
                    <select value={periodicity} onChange={(e) => setPeriodicity(e.target.value as KPIPeriodicity)} className={sInput}>
                        {(Object.entries(periodicityLabels) as [KPIPeriodicity, string][]).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                </div>
            </div>
            <KPIHistoryTable periodicity={periodicity} direction={direction} unit={unit} history={history} onChange={setHistory} canEdit={canEdit} />
            <div className="flex gap-2 pt-1">
                <button onClick={onCancel} className="flex-1 rounded-xl border border-gray-200 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50">Annuler</button>
                {canEdit && <button onClick={() => { onDelete(kpi.id || kpi._id || ''); onCancel(); }} className="rounded-xl border border-red-200 bg-red-50 py-2 px-3 text-xs font-medium text-red-500 hover:bg-red-100"><Trash2 size={13} /></button>}
                <button onClick={handleSave} className="linear flex-1 rounded-xl bg-toola-500 py-2 text-xs font-medium text-white hover:bg-toola-600">Enregistrer</button>
            </div>
        </div>
    );
};

// ── Action plan row ───────────────────────────────────────────────────────────

const ActionPlanRow: React.FC<{
    planId: string;
    state: OKRState;
    onStatusChange: (id: string, s: Task['status']) => void;
    onEditTask: (task: Task) => void;
    onNewTask: (objectiveId: string) => void;
    onUpdateHours: (id: string, hours: number) => void;
}> = ({ planId, state, onStatusChange, onEditTask, onNewTask, onUpdateHours }) => {
    const [open, setOpen] = useState(false);
    const plan = state.actionPlans.find((p) => p.id === planId);
    if (!plan) return null;

    const tasks = state.tasks.filter((t) => t.actionPlanId === plan.id);
    const done = tasks.filter((t) => t.status === 'done').length;
    const owner = state.team.find((m) => m.id === plan.responsibleUserId);
    const sMap = {
        'not-started': { label: 'Non démarré', cls: 'text-gray-500 bg-gray-100' },
        'in-progress': { label: 'En cours', cls: 'text-toola-500 bg-blue-50' },
        'completed': { label: 'Terminé', cls: 'text-green-600 bg-green-50' },
    };
    const s = sMap[plan.status] ?? sMap['not-started'];
    const hasRecurrence = plan.recurrence && plan.recurrence !== 'none';

    return (
        <div className="border border-gray-100 rounded-xl overflow-hidden">
            <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 p-4 bg-gray-50/50 hover:bg-gray-50 transition-colors text-left">
                {open ? <ChevronDown size={15} className="text-gray-400 flex-shrink-0" /> : <ChevronRight size={15} className="text-gray-400 flex-shrink-0" />}
                <Clipboard size={14} className="text-toola-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-navy-700">{plan.name}</p>
                        {hasRecurrence && (
                            <span className="flex items-center gap-1 text-xs text-toola-500 bg-blue-50 px-2 py-0.5 rounded-full border border-toola-100 flex-shrink-0">
                                <RefreshCw size={10} />{RECURRENCE_LABELS[plan.recurrence!]}
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-gray-400 truncate">{plan.description}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>
                    <span className="text-xs text-gray-400 flex items-center gap-1"><TrendingUp size={11} className="text-toola-400" />{plan.estimatedKPIImpact}%</span>
                    <span className="text-xs text-gray-400 hidden sm:flex items-center gap-1"><DollarSign size={11} />{plan.budget.toLocaleString('fr-FR')}€</span>
                    <span className="text-xs text-gray-400 flex items-center gap-1"><Calendar size={11} />{new Date(plan.deadline).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}</span>
                    {owner && <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: owner.avatarColor }} title={owner.name}>{owner.avatarInitials}</div>}
                    <span className="text-xs text-gray-400">{done}/{tasks.length}</span>
                </div>
            </button>
            {open && (
                <div className="p-4 bg-white border-t border-gray-100">
                    {hasRecurrence && (
                        <div className="mb-3 flex items-center gap-2 rounded-xl bg-blue-50 border border-toola-100 px-3 py-2 text-xs text-toola-600">
                            <RefreshCw size={12} />
                            <span>Récurrence <strong>{RECURRENCE_LABELS[plan.recurrence!]?.toLowerCase()}</strong> — {tasks.length} tâche{tasks.length > 1 ? 's' : ''} générée{tasks.length > 1 ? 's' : ''} automatiquement</span>
                        </div>
                    )}
                    <TaskBoard tasks={tasks} state={state} onStatusChange={onStatusChange} onEditTask={onEditTask} onUpdateHours={onUpdateHours} />
                    <button onClick={() => onNewTask(plan.objectiveId)} className="mt-3 flex items-center gap-1 text-xs text-toola-500 hover:text-toola-600 font-medium">
                        <Plus size={13} /> Ajouter une tâche
                    </button>
                </div>
            )}
        </div>
    );
};

// ── Objective card ────────────────────────────────────────────────────────────

const ObjectiveCard: React.FC<{
    objId: string;
    state: OKRState;
    onStatusChange: (id: string, s: Task['status']) => void;
    onEditObjective: (obj: Objective) => void;
    onDeleteObjective: (id: string) => void;
    onEditKPI: (id: string, data: Record<string, any>) => void;
    onDeleteKPI: (id: string) => void;
    onEditTask: (task: Task) => void;
    onNewTask: (objectiveId: string) => void;
    onAddActionPlan: (plan: ActionPlan) => void;
    onUpdateHours: (id: string, hours: number) => void;
}> = ({ objId, state, onStatusChange, onEditObjective, onDeleteObjective, onEditKPI, onDeleteKPI, onEditTask, onNewTask, onAddActionPlan, onUpdateHours }) => {
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState<'kpis' | 'plans' | 'tasks'>('kpis');
    const [editingKPIId, setEditingKPIId] = useState<string | null>(null);
    const planModal = useDisclosure();
    const { canManageObjectives } = useOKRPermissions();

    const obj = state.objectives.find((o) => o.id === objId);
    if (!obj) return null;

    const canManage = canManageObjectives;

    const kpis = state.kpis.filter((k) => obj.kpiIds.includes(k.id) || obj.kpiIds.includes(k._id ?? ''));
    const plans = state.actionPlans.filter((p) => obj.actionPlanIds.includes(p.id) || p.objectiveId === objId);
    const tasks = state.tasks.filter((t) => t.objectiveId === objId || String(t.objectiveId) === objId);
    const progress = computeObjectiveProgress(obj, state.kpis);
    const progressColor = progress >= 70 ? '#17ad37' : progress >= 40 ? '#FFB547' : '#ea0606';

    const catCls: Record<string, string> = {
        Qualité: 'bg-purple-50 text-purple-600', Commercial: 'bg-blue-50 text-toola-500',
        Logistique: 'bg-orange-50 text-orange-600', Service: 'bg-teal-50 text-teal-600',
    };
    const cc = catCls[obj.category] ?? 'bg-gray-100 text-gray-600';

    return (
        <>
        <div className={`rounded-2xl border bg-white shadow-sm overflow-hidden transition-shadow hover:shadow-md ${open ? 'border-toola-200' : 'border-gray-100'}`}>
            <div className="w-full text-left p-5">
                <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${cc}`}>{obj.category}</span>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${priorityColors[obj.priority]}`}>{priorityLabels[obj.priority]}</span>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColors[obj.status]}`}>{statusLabels[obj.status]}</span>
                        </div>
                        <h3 className="text-base font-bold text-navy-700 mb-1">{obj.name}</h3>
                        <p className="text-sm text-gray-500 leading-relaxed line-clamp-2">{obj.description}</p>
                    </div>
                    <div className="flex items-start gap-2 flex-shrink-0">
                        {canManage && (
                            <div className="flex items-center gap-1">
                                <button onClick={(e) => { e.stopPropagation(); onEditObjective(obj); }} className="p-1.5 rounded-lg text-gray-400 hover:text-toola-500 hover:bg-blue-50 transition-colors" title="Modifier">
                                    <Pencil size={14} />
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); onDeleteObjective(obj.id); }} className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors" title="Supprimer">
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        )}
                        <button onClick={() => setOpen(!open)} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 transition-colors">
                            {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                        </button>
                        <div className="text-right">
                            <span className="text-2xl font-extrabold" style={{ color: progressColor }}>{progress}%</span>
                            <p className="text-xs text-gray-400">progression</p>
                        </div>
                    </div>
                </div>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><User size={12} />{getTeamMemberName(obj.responsibleUserId || obj.ownerId || '', state)}</span>
                    <span className="flex items-center gap-1"><Calendar size={12} />{new Date(obj.endDate).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}</span>
                    <span className="flex items-center gap-1"><BarChart2 size={12} />{kpis.length} KPI{kpis.length > 1 ? 's' : ''}</span>
                    <span className="flex items-center gap-1"><Target size={12} />{tasks.filter((t) => t.status === 'done').length}/{tasks.length} tâches</span>
                </div>
                <div className="mt-3">
                    <div className="flex items-center gap-1.5 mb-1.5">
                        {kpis.map((k) => { const h = computeKPIHealth(k); const c = (healthColors as any)[h]; return <span key={k.id} title={k.name} className="w-2 h-2 rounded-full" style={{ backgroundColor: c }} />; })}
                    </div>
                    <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, backgroundColor: progressColor }} />
                    </div>
                </div>
            </div>

            {open && (
                <div className="border-t border-gray-100">
                    <div className="flex border-b border-gray-100 bg-gray-50/50 overflow-x-auto">
                        {[
                            { key: 'kpis', label: `KPIs (${kpis.length})`, icon: BarChart2 },
                            { key: 'plans', label: `Plans (${plans.length})`, icon: Clipboard },
                            { key: 'tasks', label: `Tâches (${tasks.length})`, icon: Zap },
                        ].map(({ key, label, icon: Icon }) => (
                            <button key={key} onClick={() => setTab(key as any)} className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${tab === key ? 'border-toola-500 text-toola-500' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                                <Icon size={14} /> {label}
                            </button>
                        ))}
                    </div>

                    <div className="p-5">
                        {tab === 'kpis' && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {kpis.map((kpi) => (
                                    editingKPIId === kpi.id ? (
                                        <KPIEditRow
                                            key={kpi.id}
                                            kpi={kpi}
                                            canEdit={canManage}
                                            onSave={(id, data) => { onEditKPI(id, data); setEditingKPIId(null); }}
                                            onDelete={(id) => { onDeleteKPI(id); setEditingKPIId(null); }}
                                            onCancel={() => setEditingKPIId(null)}
                                        />
                                    ) : (
                                        <div key={kpi.id} className="relative group">
                                            <KPICard kpi={kpi} />
                                            {canManage && (
                                                <button
                                                    onClick={() => setEditingKPIId(kpi.id)}
                                                    className="absolute top-2 right-2 p-1.5 rounded-lg bg-white border border-gray-200 text-gray-400 hover:text-toola-500 hover:border-toola-300 shadow-sm opacity-0 group-hover:opacity-100 transition-all"
                                                    title="Modifier ce KPI"
                                                >
                                                    <Pencil size={12} />
                                                </button>
                                            )}
                                        </div>
                                    )
                                ))}
                            </div>
                        )}

                        {tab === 'plans' && (
                            <div className="space-y-3">
                                {plans.map((plan) => (
                                    <ActionPlanRow key={plan.id} planId={plan.id} state={state} onStatusChange={onStatusChange} onEditTask={onEditTask} onNewTask={onNewTask} onUpdateHours={onUpdateHours} />
                                ))}
                                {plans.length === 0 && (
                                    <div className="rounded-2xl border-2 border-dashed border-gray-100 p-8 text-center">
                                        <Clipboard size={32} className="mx-auto mb-2 text-gray-200" />
                                        <p className="text-sm text-gray-400">Aucun plan d'action</p>
                                        <p className="text-xs text-gray-300 mt-1">Créez un plan pour structurer vos actions</p>
                                    </div>
                                )}
                                {canManage && (
                                    <button
                                        onClick={planModal.onOpen}
                                        className="flex items-center gap-2 text-sm font-medium text-toola-500 hover:text-toola-600 border border-toola-200 hover:border-toola-400 bg-blue-50/50 hover:bg-blue-50 rounded-xl px-4 py-2.5 transition-all"
                                    >
                                        <Plus size={15} /> Créer un plan d'action
                                    </button>
                                )}
                            </div>
                        )}

                        {tab === 'tasks' && (
                            <div>
                                <TaskBoard tasks={tasks} state={state} onStatusChange={onStatusChange} onEditTask={onEditTask} onUpdateHours={onUpdateHours} />
                                <button onClick={() => onNewTask(objId)} className="mt-3 flex items-center gap-1 text-xs text-toola-500 hover:text-toola-600 font-medium">
                                    <Plus size={13} /> Ajouter une tâche à cet objectif
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>

        {/* Plan creation modal — per objective */}
        <CreateActionPlanModal
            isOpen={planModal.isOpen}
            onClose={planModal.onClose}
            objectiveId={obj.id || obj._id || ''}
            teamMembers={state.team.map((m) => ({ id: m.id, name: m.name }))}
            onSubmit={(plan) => { onAddActionPlan(plan); planModal.onClose(); }}
        />
        </>
    );
};

// ── Panel ─────────────────────────────────────────────────────────────────────

const ObjectivesPanel: React.FC<{
    state: OKRState;
    onStatusChange: (id: string, s: Task['status']) => void;
    onEditObjective: (obj: Objective) => void;
    onDeleteObjective: (id: string) => void;
    onEditKPI: (id: string, data: Record<string, any>) => void;
    onDeleteKPI: (id: string) => void;
    onEditTask: (task: Task) => void;
    onNewTask: (objectiveId?: string) => void;
    onAddActionPlan: (plan: ActionPlan) => void;
    onUpdateHours: (id: string, hours: number) => void;
}> = ({ state, onStatusChange, onEditObjective, onDeleteObjective, onEditKPI, onDeleteKPI, onEditTask, onNewTask, onAddActionPlan, onUpdateHours }) => {
    const { isOwner, currentUserId } = useOKRPermissions();
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterPriority, setFilterPriority] = useState('all');

    const ownedObjectives = isOwner
        ? state.objectives
        : state.objectives.filter((o) => o.responsibleUserId === currentUserId || (o as any).ownerId === currentUserId);

    const filtered = ownedObjectives.filter((o) =>
        (filterStatus === 'all' || o.status === filterStatus) &&
        (filterPriority === 'all' || o.priority === filterPriority)
    );

    return (
        <div>
            <div className="flex flex-wrap items-center gap-3 mb-6">
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="text-sm border border-gray-200 rounded-xl px-3 py-2 text-navy-700 focus:outline-none focus:ring-2 focus:ring-toola-400 bg-white">
                    <option value="all">Tous les statuts</option>
                    {Object.entries(statusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} className="text-sm border border-gray-200 rounded-xl px-3 py-2 text-navy-700 focus:outline-none focus:ring-2 focus:ring-toola-400 bg-white">
                    <option value="all">Toutes les priorités</option>
                    {Object.entries(priorityLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <span className="text-sm text-gray-400 ml-auto">{filtered.length} objectif{filtered.length > 1 ? 's' : ''}</span>
            </div>
            <div className="space-y-4">
                {filtered.map((o) => (
                    <ObjectiveCard
                        key={o.id}
                        objId={o.id}
                        state={state}
                        onStatusChange={onStatusChange}
                        onEditObjective={onEditObjective}
                        onDeleteObjective={onDeleteObjective}
                        onEditKPI={onEditKPI}
                        onDeleteKPI={onDeleteKPI}
                        onEditTask={onEditTask}
                        onNewTask={onNewTask}
                        onAddActionPlan={onAddActionPlan}
                        onUpdateHours={onUpdateHours}
                    />
                ))}
                {filtered.length === 0 && (
                    <div className="rounded-2xl border-2 border-dashed border-gray-100 p-16 text-center">
                        <Target size={40} className="mx-auto mb-3 text-gray-200" />
                        <p className="text-gray-400 font-medium">Aucun objectif trouvé</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ObjectivesPanel;
